import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CirclePlay, Code2, Copy, Globe, LockKeyhole, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  fetchGithubCodes,
  createGithubCode,
  updateGithubCode,
  deleteGithubCode,
  getUniqueTags,
  codePath,
  codeShareUrl,
  slugify,
  isValidSlug,
  RESERVED_SLUGS,
} from '../../services/githubService';
import { seoConfig } from '../../configs/seoConfig';
import './GitHubCodePage.css';
import { Table, Modal, Popconfirm, TagsInput, Form, Field, useForm, toast } from '../../ui';
import { PageHeader, StatCard, FilterBar, LoadingState, ErrorState } from './components';
import EmptyState from '../../components/site/EmptyState';
import { formatDate } from '../../utils/format';
import { isValidURL } from './utils/helpers';

const urlRule = (message, required = false) => ({
  validator: (_, value) => {
    const text = String(value || '').trim();
    if (!text) return required ? Promise.reject(new Error(message)) : Promise.resolve();
    return isValidURL(text) ? Promise.resolve() : Promise.reject(new Error(message));
  },
});

const EMPTY_VALUES = {
  title: '',
  description: '',
  tags: [],
  author: '',
  link: '',
  videoLink: '',
  demoLink: '',
  slug: '',
  requireSignIn: false,
};

const toFormValues = code => ({
  title: code.title || '',
  description: code.description || '',
  tags: code.tags || [],
  author: code.author || '',
  link: code.link || '',
  videoLink: code.videoLink || '',
  demoLink: code.demoLink || '',
  slug: code.slug || '',
  requireSignIn: Boolean(code.requireSignIn),
});

// "projectskart.in/code/" shown in front of the link-name field. Read when shown: the site URL is a setting.
const sharePrefix = () => `${new URL(seoConfig.siteUrl).host.replace(/^www\./, '')}/code/`;

const slugRule = {
  validator: (_, value) => {
    const slug = String(value || '').trim();
    if (!slug) return Promise.resolve();
    if (!isValidSlug(slug))
      return Promise.reject(new Error('Use 3–80 lowercase letters, numbers and hyphens'));
    if (RESERVED_SLUGS.includes(slug)) return Promise.reject(new Error('That link name is reserved'));
    return Promise.resolve();
  },
};

// Typing "Todo App" in the link-name field becomes "todo-app".
const normalizeSlug = value =>
  String(value || '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');

const copyShareLink = async code => {
  try {
    await navigator.clipboard.writeText(codeShareUrl(code));
    toast.success('Link copied. Paste it in your YouTube description.');
  } catch {
    toast.error(`Couldn't copy. The link is ${codeShareUrl(code)}`);
  }
};

const ExternalIcon = ({ href, label, children }) =>
  href ? (
    <a className="adm-icon-btn" href={href} target="_blank" rel="noopener noreferrer" aria-label={`${label} (opens in a new tab)`} title={label}>
      {children}
    </a>
  ) : null;

/** Add / edit form, mounted fresh for each open. */
const GithubCodeForm = ({ code, onCancel, onSaved }) => {
  const form = useForm();
  const [saving, setSaving] = useState(false);
  const initialValues = useMemo(() => (code ? toFormValues(code) : EMPTY_VALUES), [code]);

  // Shown under the link-name field so the share link is visible before saving.
  const typedSlug = String(form.getFieldValue('slug') || '').trim();
  const previewSlug = typedSlug || code?.slug || slugify(form.getFieldValue('title'));

  const handleFinish = async values => {
    setSaving(true);
    const result = code ? await updateGithubCode(code.id, values) : await createGithubCode(values);
    setSaving(false);
    if (result.success) {
      const saved = result.data?.slug ? result.data : null;
      toast.success(saved ? `Saved. Share link: ${codeShareUrl(saved)}` : result.message || 'Saved');
      onSaved();
    } else {
      toast.error(result.error || "Couldn't save the entry.");
    }
  };

  return (
    <Form form={form} initialValues={initialValues} onFinish={handleFinish}>
      <Field
        name="title"
        label="Title"
        rules={[
          { required: true, whitespace: true, message: 'Enter a title' },
          { min: 3, max: 200, message: 'Use 3 to 200 characters' },
        ]}
      >
        <input className="pk-input" maxLength={200} />
      </Field>
      <Field name="description" label="Description" rules={[{ required: true, whitespace: true, message: 'Enter a description' }]}>
        <textarea className="pk-textarea" rows={4} maxLength={5000} />
      </Field>
      <div className="pk-form__row">
        <Field name="author" label="Author" rules={[{ required: true, whitespace: true, message: 'Enter the author' }]}>
          <input className="pk-input" maxLength={100} />
        </Field>
        <Field
          name="tags"
          label="Tags"
          rules={[{ type: 'array', max: 20, message: 'Use 20 tags or fewer' }]}
          help="Press Enter after each one."
        >
          <TagsInput placeholder="e.g. React" tokenSeparators={[]} />
        </Field>
      </div>
      <Field
        name="link"
        label="Code link"
        rules={[
          { required: true, whitespace: true, message: 'Enter the code link' },
          urlRule('Enter the code link, e.g. https://github.com/owner/repo', true),
        ]}
        help="A GitHub repository, a Google Drive file, or any download link."
      >
        <input className="pk-input" type="url" inputMode="url" placeholder="https://github.com/owner/repo" spellCheck={false} />
      </Field>
      <Field
        name="slug"
        label={
          <>
            Link name <span className="pk-optional">(optional)</span>
          </>
        }
        rules={[slugRule]}
        normalize={normalizeSlug}
        help={
          <>
            Share link: <strong>{`${sharePrefix()}${previewSlug}`}</strong>
            {!typedSlug && !code?.slug && ' (made from the title)'}
          </>
        }
      >
        {field => (
          <div className="gcp-prefix">
            <span className="gcp-prefix__text" aria-hidden="true">
              {sharePrefix()}
            </span>
            <input {...field} maxLength={80} placeholder={code?.slug || slugify(form.getFieldValue('title'))} spellCheck={false} autoCapitalize="off" />
          </div>
        )}
      </Field>
      <Field name="requireSignIn" valuePropName="checked">
        {field => (
          <label className="pk-check">
            <input type="checkbox" {...field} />
            <span>
              <strong>Require a free account to get the code.</strong> The page and video stay public; visitors sign in or sign up before the code link appears.
            </span>
          </label>
        )}
      </Field>
      <div className="pk-form__row">
        <Field
          name="demoLink"
          label={
            <>
              Live demo <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[urlRule('Enter a full URL that starts with https://')]}
        >
          <input className="pk-input" type="url" inputMode="url" placeholder="https://" />
        </Field>
        <Field
          name="videoLink"
          label={
            <>
              Video <span className="pk-optional">(optional)</span>
            </>
          }
          rules={[urlRule('Enter a full URL that starts with https://')]}
        >
          <input className="pk-input" type="url" inputMode="url" placeholder="https://www.youtube.com/watch?v=" />
        </Field>
      </div>
      <div className="adm-form-foot">
        <button type="button" className="pk-btn pk-btn--secondary pk-btn--sm" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="pk-btn pk-btn--primary pk-btn--sm" disabled={saving} aria-busy={saving || undefined}>
          {code ? 'Save changes' : 'Add entry'}
        </button>
      </div>
    </Form>
  );
};

const GitHubCodePage = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [tagFilter, setTagFilter] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchGithubCodes();
    if (result.success) setCodes(result.data);
    else setError(result.error);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = code => {
    setEditing(code);
    setModalOpen(true);
  };

  const handleSaved = () => {
    setModalOpen(false);
    load();
  };

  const handleDelete = async code => {
    const result = await deleteGithubCode(code.id);
    if (result.success) {
      setCodes(list => list.filter(c => c.id !== code.id));
      toast.success('Entry deleted');
    } else {
      toast.error(result.error);
    }
  };

  const tags = useMemo(() => getUniqueTags(codes), [codes]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return codes.filter(code => {
      if (tagFilter && !(code.tags || []).some(t => t.toLowerCase() === tagFilter.toLowerCase())) return false;
      if (!term) return true;
      return [code.title, code.description, code.author, code.link, code.slug, ...(code.tags || [])].some(v =>
        String(v || '').toLowerCase().includes(term)
      );
    });
  }, [codes, search, tagFilter]);

  const stats = useMemo(
    () => ({
      total: codes.length,
      locked: codes.filter(c => c.requireSignIn).length,
      withDemo: codes.filter(c => (c.demoLink || '').trim()).length,
      withVideo: codes.filter(c => (c.videoLink || '').trim()).length,
    }),
    [codes]
  );

  const columns = [
    {
      title: 'Project',
      key: 'title',
      width: 280,
      sorter: (a, b) => (a.title || '').localeCompare(b.title || ''),
      render: (_, record) => (
        <div className="adm-cell__text">
          <div className="adm-cell-title">{record.title}</div>
          <div className="adm-cell-sub adm-clamp-2">{record.description}</div>
        </div>
      ),
    },
    {
      title: 'Share link',
      key: 'share',
      width: 250,
      render: (_, record) => (
        <div className="gcp-share">
          {record.requireSignIn && (
            <LockKeyhole className="gcp-share__lock" aria-label="Requires sign-in" />
          )}
          <a
            className="gcp-share__path"
            href={codePath(record)}
            target="_blank"
            rel="noopener noreferrer"
            title={codeShareUrl(record)}
          >
            {codePath(record)}
          </a>
          <button
            type="button"
            className="adm-icon-btn"
            aria-label={`Copy share link for ${record.title}`}
            title="Copy share link"
            onClick={() => copyShareLink(record)}
          >
            <Copy aria-hidden="true" />
          </button>
        </div>
      ),
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      width: 200,
      render: list =>
        list?.length ? (
          <div className="adm-tags">
            {list.slice(0, 4).map(tag => (
              <span key={tag} className="adm-tag">
                {tag}
              </span>
            ))}
            {list.length > 4 && <span className="adm-tag">+{list.length - 4}</span>}
          </div>
        ) : (
          <span className="pk-muted">—</span>
        ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      width: 150,
    },
    {
      title: 'Links',
      key: 'links',
      width: 130,
      render: (_, record) => (
        <div className="adm-actions">
          <ExternalIcon href={record.link} label="Code link">
            <Code2 aria-hidden="true" />
          </ExternalIcon>
          <ExternalIcon href={(record.demoLink || '').trim()} label="Live demo">
            <Globe aria-hidden="true" />
          </ExternalIcon>
          <ExternalIcon href={(record.videoLink || '').trim()} label="Video">
            <CirclePlay aria-hidden="true" />
          </ExternalIcon>
        </div>
      ),
    },
    {
      title: 'Added',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      defaultSortOrder: 'descend',
      sorter: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      render: date => <span className="adm-nowrap">{formatDate(date)}</span>,
    },
    {
      title: <span className="sr-only">Actions</span>,
      key: 'actions',
      width: 96,
      fixed: 'right',
      render: (_, record) => (
        <div className="adm-actions">
          <button type="button" className="adm-icon-btn" aria-label={`Edit ${record.title}`} title="Edit" onClick={() => openEdit(record)}>
            <Pencil aria-hidden="true" />
          </button>
          <Popconfirm
            title="Delete this entry?"
            description="Only the listing is removed; the repository and anyone's access to it stay as they are."
            okText="Delete"
            okDanger
            onConfirm={() => handleDelete(record)}
          >
            <button type="button" className="adm-icon-btn" data-tone="danger" aria-label={`Delete ${record.title}`} title="Delete">
              <Trash2 aria-hidden="true" />
            </button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  let body;
  if (loading) {
    body = <LoadingState label="Loading free code" />;
  } else if (error) {
    body = (
      <div className="adm-panel__state">
        <ErrorState title="Couldn't load free code" message={error} onRetry={load} />
      </div>
    );
  } else if (codes.length === 0) {
    body = (
      <div className="adm-panel__state">
        <EmptyState
          icon={<Code2 />}
          title="No free code yet"
          text="Add the code from a video. It gets its own page and a share link for the video description."
          actions={
            <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={openCreate}>
              <Plus aria-hidden="true" />
              Add entry
            </button>
          }
        />
      </div>
    );
  } else {
    body = (
      <>
        <FilterBar
          inline
          searchLabel="Search free code"
          searchPlaceholder="Search title, link name, author, tag or URL"
          searchValue={search}
          onSearchChange={setSearch}
          filters={
            tags.length
              ? [
                  {
                    key: 'tag',
                    component: (
                      <select className="pk-select" aria-label="Filter by tag" value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
                        <option value="">All tags</option>
                        {tags.map(tag => (
                          <option key={tag} value={tag}>
                            {tag}
                          </option>
                        ))}
                      </select>
                    ),
                  },
                ]
              : []
          }
          onClearFilters={
            search || tagFilter
              ? () => {
                  setSearch('');
                  setTagFilter('');
                }
              : undefined
          }
          end={<span className="adm-toolbar__count">{filtered.length} shown</span>}
        />
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="id"
          scroll={{ x: 1280 }}
          emptyText="Nothing matches these filters."
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          }}
        />
      </>
    );
  }

  return (
    <div>
      <PageHeader
        title="Free code"
        description="Code you share from YouTube. Each entry gets its own page at /code; copy its share link into the video description."
        actions={
          <button type="button" className="pk-btn pk-btn--primary pk-btn--sm" onClick={openCreate}>
            <Plus aria-hidden="true" />
            Add entry
          </button>
        }
      />

      {!error && (
        <div className="adm-stats">
          <StatCard title="Entries" value={stats.total} icon={<Code2 />} loading={loading} />
          <StatCard title="Need sign-in" value={stats.locked} icon={<LockKeyhole />} loading={loading} />
          <StatCard title="With a video" value={stats.withVideo} icon={<CirclePlay />} loading={loading} />
          <StatCard title="With a live demo" value={stats.withDemo} icon={<Globe />} loading={loading} />
        </div>
      )}

      <section className="adm-panel" aria-label="Free code">
        {body}
      </section>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit entry' : 'Add free code'}
        footer={null}
        width={720}
        className="adm-modal"
        maskClosable={false}
      >
        <GithubCodeForm code={editing} onCancel={() => setModalOpen(false)} onSaved={handleSaved} />
      </Modal>
    </div>
  );
};

export default GitHubCodePage;
